import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  getClientsByLawyerWithFilters,
  getClientStatsForLawyer,
} from "@/shared/db";

export interface ClientWithProfile {
  id: string;
  status: string;
  client_type: string;
  phone?: string;
  company_name?: string;
  address?: string;
  created_at: string;
  updated_at: string;
  profiles: {
    id: string;
    full_name: string;
    email: string;
    country: string;
    profile_image_url?: string;
  } | null;
}

export interface ClientStats {
  total: number;
  active: number;
  inactive: number;
  newThisMonth: number;
  byType: {
    individual: number;
    business: number;
    nonprofit: number;
  };
}

export type FilterStatus = "all" | "active" | "inactive";

interface UseClientsOptions {
  lawyerId?: string;
}

export const useClients = ({ lawyerId }: UseClientsOptions) => {
  const { t } = useTranslation();
  const [clients, setClients] = useState<ClientWithProfile[]>([]);
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters state
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<FilterStatus>("all");
  const [clientTypeFilter, setClientTypeFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");

  const fetchData = useCallback(
    async (isRefresh = false) => {
      if (!lawyerId) return;

      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        // Load clients with filters
        const clientsData = await getClientsByLawyerWithFilters(lawyerId, {
          status: statusFilter === "all" ? undefined : statusFilter,
          client_type: clientTypeFilter || undefined,
          country: countryFilter || undefined,
          search: searchQuery || undefined,
        });

        setClients(clientsData as unknown as ClientWithProfile[]);

        // Load stats
        const statsData = await getClientStatsForLawyer(lawyerId);
        setStats({
          total: statsData.total,
          active: statsData.active,
          inactive: statsData.inactive,
          newThisMonth: 0, // Not available in API currently
          byType: {
            individual: statsData.individual,
            business: statsData.business,
            nonprofit: statsData.nonprofit,
          },
        });
      } catch (error) {
        console.error("Error loading clients:", error);
        toast.error(t("lawyer.clients.loadError", "Error al cargar clientes"));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [lawyerId, searchQuery, statusFilter, clientTypeFilter, countryFilter, t]
  );

  // Initial load and filter changes
  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  const refresh = () => fetchData(true);

  return {
    clients,
    stats,
    loading,
    refreshing,
    refresh,
    filters: {
      searchQuery,
      setSearchQuery,
      statusFilter,
      setStatusFilter,
      clientTypeFilter,
      setClientTypeFilter,
      countryFilter,
      setCountryFilter,
    },
  };
};
